'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { createContext, type ReactNode, useContext, useRef, useState } from 'react'
import { useStore } from 'zustand'

import { createGlobalStore, type GlobalStore } from './store'

export type GlobalStoreApi = ReturnType<typeof createGlobalStore>
export const GlobalStoreContext = createContext<GlobalStoreApi | undefined>(undefined)

export const GlobalStoreProvider = ({ children }: { children: ReactNode }) => {
  const [queryClient] = useState(() => new QueryClient())
  const storeRef = useRef<GlobalStoreApi>(null)
  if (!storeRef.current) {
    storeRef.current = createGlobalStore()
  }
  return (
    <ThemeProvider attribute='class' enableSystem={false}>
      <GlobalStoreContext.Provider value={storeRef.current}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </GlobalStoreContext.Provider>
    </ThemeProvider>
  )
}

export const useGlobalStore = <T,>(selector: (store: GlobalStore) => T): T => {
  const globalStoreContext = useContext(GlobalStoreContext)
  if (!globalStoreContext) {
    throw new Error(`useGlobalStore must be used within GlobalStoreProvider`)
  }
  return useStore(globalStoreContext, selector)
}
